import React, { useState, useEffect, SetStateAction, Dispatch } from "react";
import { Box, Button, IconButton, Switch, Typography } from "@mui/material";
import TableComponent from "../../components/TableComponent";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { ENUM_ROUTES } from "../../RoutesEnum";
import {
  useDeleteDiscountMutation,
  useLazyGetDiscountsQuery,
  useUpdateDiscountMutation,
} from "../../services/discount";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/ConfirmationModal";
import CustomToggle from "../../helpers/CustomToggle";
import toast from "react-hot-toast";

export interface DiscountResponse {
  _id: string;
  name: string;
  discountType: string;
  applicableTime: string;
  minBookingAmount: number;
  startDate: string;
  endDate: string;
  venueStorageId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  latitude?: number;
  longitude?: number;
  couponCode?: string;
}

export type Root = DiscountResponse[];
interface DiscountProps {
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  setDebouncedSearchTerm: Dispatch<SetStateAction<string>>;
  selectedType: "VENUE" | "STORAGE";
}
const DiscountList = ({ searchTerm, selectedType }: DiscountProps) => {
  const navigate = useNavigate();
  const [getDiscout] = useLazyGetDiscountsQuery();
  const [discountData, setDiscountData] = useState<DiscountResponse[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDiscount] = useDeleteDiscountMutation();
  const [updateDiscount] = useUpdateDiscountMutation();
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const fetchDiscount = async () => {
    try {
      const response = await getDiscout({
        from: page,
        size: pageSize,
        search: searchTerm,
        type: selectedType,
      }).unwrap();

      if (response?.statusCode === 200) {
        setDiscountData(response.data.data || []);
        setTotalCount(response?.data?.total || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch discounts");
    }
  };

  const deleteCoponApi = async (id: string) => {
    setLoading(true);
    try {
      const response = await deleteDiscount({
        id,
      }).unwrap();
      setLoading(false);
      if (response?.statusCode === 200) {
        showToast(response?.message || "Discount Deleted Successfully");
        toast.dismiss()
        fetchDiscount();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setLoading(false);
    }
  };

  const toggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      const response = await updateDiscount({
        id,
        body: { isActive: !currentStatus },
      }).unwrap();

      if (response?.statusCode === 200) {
        showToast(
          `coupon ${!currentStatus ? "activated" : "deactivated"} successfully`
        );
        fetchDiscount();
      } else {
        showError("Failed to update coupon status");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to update coupon status");
    }
  };
  useEffect(() => {
    fetchDiscount();
  }, [page, pageSize, selectedType, searchTerm]);

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Get discount type display text
  const getDiscountTypeText = (type: string) => {
    switch (type) {
      case "PERCENTAGE":
        return "Percentage";
      case "FIXED":
        return "Fixed Amount";
      case "BOTH":
        return "Both";
      default:
        return type;
    }
  };

  const getApplicableTimeText = (time: string) => {
    switch (time) {
      case "MORNING":
        return "Morning";
      case "EVENING":
        return "Evening";
      case "ALL_DAY":
        return "All Day";
      default:
        return time;
    }
  };

  const columns: GridColDef<DiscountResponse>[] = [
    {
      field: "name",
      headerName: "Offer Name",
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: "discountType",
      headerName: "Discount Type",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => getDiscountTypeText(params.value),
    },
    {
      field: "minBookingAmount",
      headerName: "Min. Bookings",
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      field: "startDate",
      headerName: "Valid From",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "endDate",
      headerName: "Valid To",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "couponCode",
      headerName: "Coupon Code",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "isActive",
      headerName: "Active",
      flex: 1,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params: any) => (
        <CustomToggle
          checked={params.value || false}
          onChange={() =>
            toggleStatus(params?.row?._id || params?.row?.id, params.value)
          }
          id={params?.row?._id || params?.row?.id}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <Box className="table_actions v2">
          <IconButton
            onClick={() =>
              navigate(ENUM_ROUTES.DISCOUNT_DETAILS + `/${params?.row?._id}`)
            }
          >
            <img src="/static/images/eye_icon.svg" alt="View" />
          </IconButton>
          <IconButton
            onClick={() =>
              navigate(ENUM_ROUTES.EDIT_DISCOUNT + `/${params?.row?._id}`)
            }
          >
            <img src="/static/images/edit2_icon.svg" alt="Edit" />
          </IconButton>
          <IconButton
            onClick={() => {
              setSelectedId(params?.row?._id);
              setOpen(true);
            }}
          >
            <img src="/static/images/trash2_icon.svg" alt="Delete" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div className="sorting_table">
      <TableComponent
        columns={columns}
        rows={discountData}
        count={totalCount}
        page={page}
        onPageChange={handlePageChange}
      />
      <ConfirmationModal
        text={"Are you sure you want to delete this Discount Copon?"}
        openModal={open}
        setOpenModal={setOpen}
        handleSubmit={() => deleteCoponApi(selectedId)}
      />
    </div>
  );
};

export default DiscountList;
